import { getOnboardingWarnings, markOnboardingWarningsSeen } from 'frontend/core/src/data/apis/api';
import { useEffect, useRef } from 'react';

/**
 * On the first authenticated mount of a session, fetch any onboarding failures the backend
 * persisted for this user (during self-signup or an invite the user wasn't part of), show them,
 * then flush them so they aren't shown again.
 *
 * No new-vs-returning gating is needed: the backend clears the warnings on `/seen`, so subsequent
 * fetches return an empty list. The one-time-per-session ref just avoids a duplicate fetch on
 * re-render before the flush round-trips.
 */
export function useOnboardingWarnings(user?: { id?: string }) {
  const hasRun = useRef(false);

  useEffect(() => {
    async function showAndFlushOnboardingWarnings() {
      try {
        const { onboardingWarnings } = await getOnboardingWarnings();
        if (!onboardingWarnings?.length) return;

        // Lazy: the toast is only needed for the rare user who has a failure to show, so keep it
        // out of the initial-load bundle rather than eagerly importing it for every session.
        const { showOnboardingWarningsToast } = await import('@common/onboarding/OnboardingWarningsToast/util');

        // Show first; only flush once the user has actually been shown the warnings, so a failed
        // `/seen` call never silently discards an unshown warning.
        showOnboardingWarningsToast(onboardingWarnings);
        await markOnboardingWarningsSeen();
      } catch {
        // Best-effort and non-blocking — never let onboarding-warning surfacing break login.
      }
    }

    if (!user?.id || hasRun.current) return;
    hasRun.current = true;

    void showAndFlushOnboardingWarnings();
  }, [user?.id]);
}
